import React, { useState } from 'react';
import { useDispatch, useSelector } from '../../../reduxHooks';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog } from '@betareader/design-system';

import { authUtil } from '../../../utils';
import featureToggleUtil from '../../../utils/featureToggleUtil';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { fetchCurrentBook } from '../../../modules/book';

export const UPGRADE_FEATURE = 'scene_editor_upgrade';
/**
 * True only when no global FeatureFlag row exists for the feature — i.e. it is
 * still dark and a per-user grant is what decides. `getAllFeatureFlags` emits
 * one of these per flag precisely because the effective boolean reads false
 * both for "no row" and for "row explicitly turned off".
 */
const UPGRADE_BETA_ACCESS = `${UPGRADE_FEATURE}_beta_access_allowed`;

/**
 * The client mirror of the server's `isFeatureEnabledWithKillSwitch`: a global
 * row wins in BOTH directions, and only in its absence does the per-user beta
 * grant decide. Exported so the gate can be tested on its own.
 */
export const isUpgradeAvailable = (
  flags: Record<string, boolean> | undefined,
  userProfile: any
): boolean => {
  if (flags?.[UPGRADE_FEATURE] === true) return true;
  // An explicit `false` row is the kill switch: it denies beta users too, and
  // without this check they would keep being offered a button that 403s.
  if (flags?.[UPGRADE_BETA_ACCESS] !== true) return false;
  return featureToggleUtil.hasFeatureAccess(userProfile, UPGRADE_FEATURE);
};

interface Props {
  bookId: string;
}

type UpgradeResult =
  | { kind: 'complete'; chaptersConverted: number; commentsReanchored: number }
  | { kind: 'confirmed' };

/**
 * "Upgrade this book" — converts a legacy Draft.js book to the scene editor
 * (docs/features/draftjs-tiptap-upgrade). Author-initiated, one book at a
 * time; the server keeps a backup version of the original and leaves inline
 * comments anchored.
 *
 * The gate mirrors the server's `isFeatureEnabledForUserWithKillSwitch` — see
 * `isUpgradeAvailable`. Checking only the global flag would leave beta-granted
 * authors with an API that answers 200 and a UI that never offers the action;
 * ORing the two would leave them with a button that 403s after the kill switch
 * is pulled.
 */
const BookFormatUpgrade: React.FC<Props> = ({ bookId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const currentBook = useSelector((state: any) => state.book.currentBook);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UpgradeResult | null>(null);

  // Nothing to offer until the flags have loaded — rendering first would flash
  // the action at authors who do not have it.
  if (flagsLoading) return null;
  if (!isUpgradeAvailable(flags, userProfile)) return null;
  if (!currentBook) return null;
  // Author only — the same comparison viewBookDetails uses. Collaborators can
  // edit chapters but should not be able to change the book's storage format.
  const isAuthor =
    userProfile &&
    currentBook.author &&
    currentBook.author._id === userProfile._id;
  if (!isAuthor) return null;
  // Nothing to offer once the book is already on the new format.
  if (currentBook.contentFormat === 'tiptap' && !result) return null;

  const bookUrl = `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}`;
  const refreshBook = (idToken: string) => {
    dispatch(
      fetchCurrentBook({
        idToken,
        bookId,
        token: undefined,
        contentVersionId: undefined,
      })
    );
  };

  const runUpgrade = async () => {
    setBusy(true);
    setError(null);
    let idToken: string | undefined;
    let requestStarted = false;
    try {
      idToken = await authUtil.getFreshIdToken();
      if (!idToken) {
        setError(t('UpgradeBookFormatSignInRequired'));
        return;
      }
      requestStarted = true;
      const response = await fetch(`${bookUrl}/upgrade-format`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'br-token': idToken,
        },
        body: JSON.stringify({}),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        if (body?.msg || body?.err) {
          setError(body.msg || body.err);
          return;
        }
        // A proxy error may arrive without JSON after the server committed.
        // Check the book before calling the outcome a failure.
        throw new Error(`HTTP ${response.status}`);
      }
      if (!body) {
        // A successful response without a readable body is also ambiguous.
        throw new Error('Missing upgrade response');
      }
      setResult({
        kind: 'complete',
        chaptersConverted: body.chaptersConverted ?? 0,
        commentsReanchored: body.commentsReanchored ?? 0,
      });
      setConfirmOpen(false);
      refreshBook(idToken);
    } catch (e) {
      if (!requestStarted || !idToken) {
        setError(t('UpgradeBookFormatSignInRequired'));
        return;
      }
      // The POST may have finished even if the browser lost its response.
      // A read checks the committed format without repeating the mutation.
      try {
        const check = await fetch(bookUrl, {
          cache: 'no-store',
          headers: { 'br-token': idToken },
        });
        if (check.ok && (await check.json()).contentFormat === 'tiptap') {
          setResult({ kind: 'confirmed' });
          setConfirmOpen(false);
          refreshBook(idToken);
          return;
        }
      } catch (_) {
        // The outcome is still unknown; ask the author to reload first.
      }
      setError(t('UpgradeBookFormatOutcomeUnknown'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="book-format-upgrade">
      {result && (
        <Alert variant="success" data-testid="book-format-upgrade-done">
          <strong>{t('UpgradeBookFormatDoneHeader')}</strong>
          <p>
            {result.kind === 'complete'
              ? t('UpgradeBookFormatDoneBody', {
                  chapters: result.chaptersConverted,
                  comments: result.commentsReanchored,
                })
              : t('UpgradeBookFormatConfirmedBody')}
          </p>
        </Alert>
      )}

      {!result && (
        <Button
          variant="secondary"
          data-testid="book-format-upgrade-button"
          onClick={() => {
            setError(null);
            setConfirmOpen(true);
          }}
        >
          {t('UpgradeBookFormat')}
        </Button>
      )}

      <Dialog
        open={confirmOpen}
        title={t('UpgradeBookFormat')}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              {t('Cancel')}
            </Button>
            <Button
              aria-busy={busy}
              disabled={busy}
              data-testid="book-format-upgrade-confirm"
              onClick={runUpgrade}
            >
              {t('UpgradeBookFormatConfirm')}
            </Button>
          </>
        }
      >
        <p>{t('UpgradeBookFormatExplanation')}</p>
        <p>{t('UpgradeBookFormatBackupNote')}</p>
        {error && (
          <Alert variant="error" data-testid="book-format-upgrade-error">
            {error}
          </Alert>
        )}
      </Dialog>
    </div>
  );
};

export default BookFormatUpgrade;
